import { SelectHTMLAttributes } from 'react'

import {
  CreatableDropdown,
  Dropdown as JoorDropdown,
} from '@joor/design-system'
import { useField } from 'formik'

import { BaseProps } from './Field'

export type Option<ValueType, LabelType> = {
  value: ValueType
  label: LabelType
  isDisabled?: boolean
}

export interface PropsType<ValueType, LabelType>
  extends
    BaseProps,
    Omit<
      SelectHTMLAttributes<HTMLSelectElement>,
      'name' | 'value' | 'defaultValue' | 'onChange'
    > {
  options: Option<ValueType, LabelType>[]
  defaultValue?: Option<ValueType, LabelType>
  value?: Option<ValueType, LabelType>
  onChange?: (selected: Option<ValueType, LabelType>) => void
  creatable?: boolean
  isClearable?: boolean
}

const Dropdown = <ValueType, LabelType>({
  required,
  onChange,
  creatable = false,
  isClearable = true,
  ...props
}: PropsType<ValueType, LabelType>) => {
  const [{ onBlur: _fieldOnBlur, ...field }, meta, { setValue, setTouched }] =
    useField({
      disabled: props.disabled,
      name: props.name,
    })
  const shouldShowError = meta.touched && !!meta.error
  const Component = creatable ? CreatableDropdown : JoorDropdown
  return (
    <Component
      onSelect={({ selected }: { selected: Option<ValueType, LabelType> }) => {
        if (onChange) {
          onChange(selected)
        } else {
          setValue(selected)
        }
      }}
      onBlur={() => setTouched(true)}
      onClear={() => {
        setValue(null)
      }}
      isRequired={required}
      isClearable={isClearable}
      {...(shouldShowError && { errorMessage: meta.error })}
      {...field}
      {...props}
    />
  )
}

export default Dropdown
